/**
 * ADA Job Persistence System
 * Saves and loads enriched jobs to/from JSON files (GitHub storage)
 * Follows the newsletter data management pattern
 */

import { formatDate, saveToGitHub } from './githubUtils';

// Get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get date string from Date object or use today
const getDateString = (date = null) => {
  if (!date) return getTodayDateString();
  if (typeof date === 'string') return date; // already formatted
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Construct file path for enriched jobs
const getEnrichedJobsPath = (date) => {
  const dateStr = getDateString(date);
  return `data/enriched-jobs-${dateStr}.json`;
};

// Helper: Get file from GitHub
const getFileFromGitHub = async (filePath) => {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/contents/${filePath}`,
      {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      return {
        content: atob(data.content),
        sha: data.sha,
        exists: true
      };
    }
    return { exists: false };
  } catch (error) {
    console.error('[adaJobPersistence] Error fetching from GitHub:', error);
    return { exists: false };
  }
};

// Helper: Save file to GitHub
const saveFileToGitHub = async (filePath, content, message) => {
  try {
    if (!process.env.REACT_APP_GITHUB_TOKEN) {
      console.warn('[adaJobPersistence] GitHub token not configured');
      return false;
    }

    // First, try to get existing SHA
    const existing = await getFileFromGitHub(filePath);
    
    const requestBody = {
      message: message,
      content: btoa(unescape(encodeURIComponent(content))),
      branch: 'main'
    };

    if (existing.sha) {
      requestBody.sha = existing.sha;
    }

    const response = await fetch(
      `https://api.github.com/repos/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/contents/${filePath}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
          'Accept': 'application/vnd.github.v3+json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      console.error('[adaJobPersistence] GitHub API error:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[adaJobPersistence] Error saving to GitHub:', error);
    return false;
  }
};

/**
 * Load enriched jobs for a specific date from GitHub
 * @param {string|Date} date - Date to load (YYYY-MM-DD or Date object), defaults to today
 * @returns {Promise<Array>} Array of enriched job objects
 */
export const loadEnrichedJobs = async (date = null) => {
  try {
    const filePath = getEnrichedJobsPath(date);
    const fileData = await getFileFromGitHub(filePath);
    
    if (!fileData.exists) {
      console.log(`[adaJobPersistence] No jobs file found for date: ${getDateString(date)}`);
      return [];
    }

    if (!fileData.content) {
      console.log(`[adaJobPersistence] Empty jobs file: ${filePath}`);
      return [];
    }

    const parsed = JSON.parse(fileData.content);
    console.log(`[adaJobPersistence] Loaded ${parsed.length} enriched jobs from ${filePath}`);
    return parsed;
  } catch (error) {
    console.error('[adaJobPersistence] Error loading enriched jobs:', error);
    return [];
  }
};

/**
 * Save enriched jobs to GitHub JSON file
 * @param {Array} jobs - Array of enriched job objects
 * @param {string|Date} date - Date to save (YYYY-MM-DD or Date object), defaults to today
 * @returns {Promise<boolean>} Success status
 */
export const saveEnrichedJobs = async (jobs, date = null) => {
  try {
    if (!Array.isArray(jobs)) {
      console.error('[adaJobPersistence] Jobs must be an array');
      return false;
    }

    const filePath = getEnrichedJobsPath(date);
    const dateStr = getDateString(date);
    const content = JSON.stringify(jobs, null, 2);
    const message = `Save ${jobs.length} enriched jobs for ${dateStr}`;

    const success = await saveFileToGitHub(filePath, content, message);
    if (success) {
      console.log(`[adaJobPersistence] Saved ${jobs.length} enriched jobs to ${filePath}`);
    }
    return success;
  } catch (error) {
    console.error('[adaJobPersistence] Error saving enriched jobs:', error);
    return false;
  }
};

/**
 * Get enriched jobs for a date range
 * @param {string|Date} startDate - Start date (YYYY-MM-DD or Date object)
 * @param {string|Date} endDate - End date (YYYY-MM-DD or Date object)
 * @returns {Promise<Array>} Combined array of enriched jobs from date range
 */
export const loadEnrichedJobsRange = async (startDate, endDate) => {
  try {
    const start = new Date(getDateString(startDate));
    const end = new Date(getDateString(endDate));
    const allJobs = [];
    const jobIds = new Set();

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = getDateString(d);
      const jobs = await loadEnrichedJobs(dateStr);
      
      // Avoid duplicates by tracking job IDs
      jobs.forEach(job => {
        if (!jobIds.has(job.id)) {
          jobIds.add(job.id);
          allJobs.push(job);
        }
      });
    }

    console.log(`[adaJobPersistence] Loaded ${allJobs.length} unique jobs from date range`);
    return allJobs;
  } catch (error) {
    console.error('[adaJobPersistence] Error loading date range:', error);
    return [];
  }
};

/**
 * Update specific job sourcing data in stored jobs
 * @param {string} jobId - Job ID to update
 * @param {Object} sourcingData - Sourcing data to update (candidatesApplied, candidatesQualified, etc.)
 * @param {string|Date} date - Date of jobs file, defaults to today
 * @returns {Promise<boolean>} Success status
 */
export const updateJobSourcingInStorage = async (jobId, sourcingData, date = null) => {
  try {
    const jobs = await loadEnrichedJobs(date);
    const job = jobs.find(j => j.id === jobId);
    
    if (!job) {
      console.warn(`[adaJobPersistence] Job ${jobId} not found in storage`);
      return false;
    }

    // Update sourcing data
    if (!job.sourcingData) {
      job.sourcingData = {};
    }
    job.sourcingData = { ...job.sourcingData, ...sourcingData };
    job.lastSourcingUpdate = new Date().toISOString();

    return await saveEnrichedJobs(jobs, date);
  } catch (error) {
    console.error('[adaJobPersistence] Error updating job sourcing:', error);
    return false;
  }
};

/**
 * Get analytics for enriched jobs from a date
 * @param {string|Date} date - Date to get analytics for, defaults to today
 * @returns {Promise<Object>} Analytics object with counts, scores, etc.
 */
export const getEnrichedJobsAnalytics = async (date = null) => {
  try {
    const jobs = await loadEnrichedJobs(date);
    
    if (jobs.length === 0) {
      return {
        totalJobs: 0,
        top25PercentCount: 0,
        averageAdaScore: 0,
        averageRelevanceScore: 0,
        industryDistribution: {},
        tierDistribution: {},
        qualifiedCandidatesCount: 0,
        dateString: getDateString(date)
      };
    }

    const top25Count = Math.ceil(jobs.length * 0.25);
    const tierOneJobs = jobs.filter(j => j.tier === 'tier-1').slice(0, top25Count);
    
    const analytics = {
      totalJobs: jobs.length,
      top25PercentCount: top25Count,
      averageAdaScore: jobs.reduce((sum, j) => sum + (j.adaComplianceScore || 0), 0) / jobs.length,
      averageRelevanceScore: jobs.reduce((sum, j) => sum + (j.relevanceScore || 0), 0) / jobs.length,
      industryDistribution: {},
      tierDistribution: {},
      qualifiedCandidatesCount: tierOneJobs.reduce((sum, j) => sum + (j.sourcingData?.candidatesQualified || 0), 0),
      dateString: getDateString(date)
    };

    // Count by industry
    jobs.forEach(job => {
      const industry = job.industry || 'Unknown';
      analytics.industryDistribution[industry] = (analytics.industryDistribution[industry] || 0) + 1;
    });

    // Count by tier
    jobs.forEach(job => {
      const tier = job.tier || 'unknown';
      analytics.tierDistribution[tier] = (analytics.tierDistribution[tier] || 0) + 1;
    });

    return analytics;
  } catch (error) {
    console.error('[adaJobPersistence] Error getting analytics:', error);
    return {
      totalJobs: 0,
      top25PercentCount: 0,
      averageAdaScore: 0,
      averageRelevanceScore: 0,
      industryDistribution: {},
      tierDistribution: {},
      qualifiedCandidatesCount: 0,
      error: error.message
    };
  }
};

/**
 * Delete enriched jobs file for a date
 * @param {string|Date} date - Date of file to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteEnrichedJobsFile = async (date = null) => {
  try {
    const filePath = getEnrichedJobsPath(date);
    // Note: Would require GitHub API delete capability
    console.log(`[adaJobPersistence] Delete requested for: ${filePath}`);
    // TODO: Implement delete via GitHub API when needed
    return false;
  } catch (error) {
    console.error('[adaJobPersistence] Error deleting file:', error);
    return false;
  }
};

/**
 * Export enriched jobs as CSV string (for download)
 * @param {Array} jobs - Array of enriched job objects
 * @returns {string} CSV formatted string
 */
export const exportJobsAsCSV = (jobs) => {
  if (!Array.isArray(jobs) || jobs.length === 0) {
    return 'No jobs to export';
  }

  // Define CSV headers
  const headers = [
    'Job ID',
    'Title',
    'Company',
    'Industry',
    'ADA Score',
    'Relevance Score',
    'Tier',
    'Salary Range',
    'Location',
    'HR Email',
    'HR Phone',
    'Job URL',
    'Candidates Applied',
    'Candidates Qualified',
    'Date Posted'
  ];

  // Create CSV rows
  const rows = jobs.map(job => [
    job.id || '',
    job.title || '',
    job.companyName || '',
    job.industry || '',
    job.adaComplianceScore || '',
    job.relevanceScore || '',
    job.tier || '',
    job.salaryRange || '',
    job.location || '',
    job.companyEmail || '',
    job.companyPhone || '',
    job.url || '',
    job.sourcingData?.candidatesApplied || '0',
    job.sourcingData?.candidatesQualified || '0',
    job.datePosted || ''
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Get list of dates with available enriched jobs files
 * @returns {Promise<Array>} Array of date strings (YYYY-MM-DD) with available files
 */
export const getAvailableEnrichedJobsDates = async () => {
  try {
    // This would require GitHub API to list files in data/ directory
    // For now, return today's date as default
    const today = getTodayDateString();
    const available = [];
    
    const exists = await fileExists(getEnrichedJobsPath(today));
    if (exists) {
      available.push(today);
    }

    return available;
  } catch (error) {
    console.error('[adaJobPersistence] Error getting available dates:', error);
    return [];
  }
};

export default {
  loadEnrichedJobs,
  saveEnrichedJobs,
  loadEnrichedJobsRange,
  updateJobSourcingInStorage,
  getEnrichedJobsAnalytics,
  deleteEnrichedJobsFile,
  exportJobsAsCSV,
  getAvailableEnrichedJobsDates,
  getTodayDateString,
  getDateString
};
